import { MockedProvider } from '@apollo/client/testing';
import { render } from '@testing-library/react';

import { RedwoodProvider } from '@redwoodjs/web';
import { RedwoodApolloProvider } from '@redwoodjs/web/apollo';

import { AddPartnerProvider } from 'src/contexts/AddPartnerProvider';
import { VERIFY_USER_OTP_MUTATION } from 'src/graphql/mutations/login.mutation';
import { LOGGED_IN_USER_QUERY } from 'src/graphql/queries/users.query';
import ThemeCustomization from 'src/themes/index';
import { getMerchantsMockApollo } from 'src/utils/__mocks__/AddMerchantMocks/merchantMocks';
import { getPartnersMockApollo } from 'src/utils/__mocks__/AddPartnerMocks';
import { adminUserMock, verifyUserOtpMock } from 'src/utils/__mocks__/AuthMocks/AuthMocks';
import {
    getSepaMandatesMerchantApolloMock,
    getSepaMandatesPartnerApolloMock,
    sendSepaMandatesMerchantApolloMock,
} from 'src/utils/__mocks__/sepaMandateDirectDebitAgreementMocks/sepaMandateDirectDebitAgreementMocks';
import { validateEmailMockApollo } from 'src/utils/__mocks__/utilsMocks/utilsMocks';

// (KM) This fixes the typing issue for window.Cypress
declare global {
    interface Window {
        Cypress: any;
    }
}

export const isTestingWithJest = () => {
    return process.env.JEST_WORKER_ID !== undefined;
};

export const CheckTestingWithCypress = () => {
    return window.Cypress ?? false;
};

const mocks = [
    {
        request: {
            query: LOGGED_IN_USER_QUERY,
            variables: {},
        },
        result: {},
    },
    {
        request: {
            query: VERIFY_USER_OTP_MUTATION,
            variables: {},
        },
        result: verifyUserOtpMock,
    },
    getSepaMandatesMerchantApolloMock,
    getSepaMandatesPartnerApolloMock,
    getPartnersMockApollo,
    getMerchantsMockApollo,
    sendSepaMandatesMerchantApolloMock,
    validateEmailMockApollo,
];

const AllTheProviders = ({ children }) => {
    return (
        <>
            <RedwoodProvider titleTemplate="%PageTitle | %AppTitle">
                <RedwoodApolloProvider
                    useAuth={() => {
                        return { currentUser: adminUserMock };
                    }}
                >
                    <AddPartnerProvider>
                        <MockedProvider mocks={mocks}>
                            <ThemeCustomization>{children}</ThemeCustomization>
                        </MockedProvider>
                    </AddPartnerProvider>
                </RedwoodApolloProvider>
            </RedwoodProvider>
        </>
    );
};

const customRender = (ui, options = {}) => {
    return render(ui, { wrapper: AllTheProviders, ...options });
};

// re-export everything
export * from '@testing-library/react';

// override render method
export { customRender as render };

export const colorsForTesting = () => {
    return {
        paper: '#ffffff',
        primaryLight: '#F6B478',
        primary200: '#F8C799',
        primaryMain: '#F18F34',
        primaryDark: '#A15F23',
        primary800: '#79481A',
        secondaryLight: '#FEDA55',
        secondary200: '#FEE380',
        secondaryMain: '#FDC800',
        secondaryDark: '#A98500',
        secondary800: '#7F6400',
        successLight: '#6CE9A6',
        success200: '#A6F4C5',
        successMain: '#12B76A',
        successDark: '#027A48',
        errorLight: '#FDA29B',
        errorMain: '#F04438',
        errorDark: '#912018',
        orangeLight: '#F6B478',
        orangeMain: '#F18F34',
        orangeDark: '#A15F23',
        warningLight: '#FDCC67',
        warningMain: '#F79009',
        warningDark: '#B54708',
        grey50: '#f5f5f5',
        grey100: '#f5f5f5',
        grey200: '#ECECEC',
        grey300: '#E6E6E6',
        grey500: '#DADADA',
        grey600: '#B6B6B6',
        grey700: '#919191',
        grey900: '#101828',
        text100: '#B4B4B3',
        text200: '#8E8E8D',
        text300: '#686867',
        text400: '#434341',
        text500: '#1D1D1B',
        text600: '#181817',
        text700: '#000000',
        text800: '#0F0F0E',
        text900: '#000000',
        darkPaper: '#111936',
        darkBackground: '#666666',
        darkLevel1: '#29314f',
        darkLevel2: '#212946',
        darkTextTitle: '#d7dcec',
        darkTextPrimary: '#bdc8f0',
        darkTextSecondary: '#8492c4',
        darkPrimaryLight: '#e3f2fd',
        darkPrimaryMain: '#2196f3',
        darkPrimaryDark: '#1e88e5',
        darkPrimary200: '#90caf9',
        darkPrimary800: '#1565c0',
        darkSecondaryLight: '#d1c4e9',
        darkSecondaryMain: '#7c4dff',
        darkSecondaryDark: '#651fff',
        darkSecondary200: '#b39ddb',
        darkSecondary800: '#6200ea',
    };
};
